import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  DialogContentText,
  Tooltip,
  Switch,
  FormControlLabel,
  Autocomplete,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  DataGrid,
  GridActionsCellItem,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarColumnsButton,
} from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { userRolesAPI, usersAPI, rolesAPI, departmentsAPI, permissionsAPI } from '../../services/api';
import { usePermissions } from '../../contexts/PermissionContext';
import { useAuth } from '../../contexts/AuthContext';
import { AutoCloseAlert } from '../Common';
import { isActiveStatus, isInactiveStatus } from '../../config/businessConstants';

const CustomToolbar = ({ onAdd, hasCreatePermission, hasExportPermission }) => (
  <GridToolbarContainer>
    <GridToolbarColumnsButton />
    <GridToolbarFilterButton />
    {hasExportPermission && (
      <GridToolbarExport 
        printOptions={{ disableToolbarButton: true }}
        csvOptions={{ 
          disableToolbarButton: false,
          fileName: 'User_Roles_Management' // Custom filename for CSV export
        }}
      />
    )}
    {hasCreatePermission && (
      <Button startIcon={<AddIcon />} onClick={onAdd}>
        Assign Role
      </Button>
    )}
  </GridToolbarContainer>
);

const UserRoles = () => {
  const { hasPermission } = usePermissions();
  const [userRoles, setUserRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]); // State to store filtered departments based on selected user's company
  
  // Autocomplete search state for users
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [userSearchOpen, setUserSearchOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [formData, setFormData] = useState({
    user_id: '',
    role_id: '',
    department_id: '',
    effective_date: '',
    expiry_date: '',
    notes: '',
    is_active: true,
  });
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUserRoles, setSelectedUserRoles] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [mappingToDelete, setMappingToDelete] = useState(null);
  
  // New state variables for user role validation
  const [userRoleCheck, setUserRoleCheck] = useState(null);
  const [isCheckingUser, setIsCheckingUser] = useState(false);
  const [formError, setFormError] = useState('');
  
  // Form validation state
  const [formErrors, setFormErrors] = useState({
    user_id: '',
    department_id: '',
    role_id: '',
  });

  // Pagination state
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);

  const canView = hasPermission('UserRoles', 'view');
  const canCreate = hasPermission('UserRoles', 'create');
  const canUpdate = hasPermission('UserRoles', 'update');
  const canDelete = hasPermission('UserRoles', 'delete');
  const canExport = hasPermission('UserRoles', 'export');

  // Validation function for form fields
  const validateForm = () => {
    const errors = {
      user_id: '',
      department_id: '',
      role_id: '',
    };
    let isValid = true;

    // Validate user selection
    if (!formData.user_id) {
      errors.user_id = 'Please select a user';
      isValid = false;
    }

    // Validate department selection
    if (!formData.department_id) {
      errors.department_id = 'Please select a department';
      isValid = false;
    }

    // Validate role selection
    if (!formData.role_id) {
      errors.role_id = 'Please select a role';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  // Validate individual field
  const validateField = (fieldName, value) => {
    const errors = {};

    switch (fieldName) {
      case 'user_id':
        if (!value) {
          errors.user_id = 'Please select a user';
        }
        break;
      case 'department_id':
        if (!value) {
          errors.department_id = 'Please select a department';
        }
        break;
      case 'role_id':
        if (!value) {
          errors.role_id = 'Please select a role';
        }
        break;
      default:
        break;
    }

    return errors;
  };


  useEffect(() => {
    if (canView) {
      loadUserRoles();
      loadUsers();
      loadRoles();
      loadDepartments();
    }
  }, [canView, paginationModel]);

  useEffect(() => {
    if (selectedUserId) {
      loadUserRoleDetails(selectedUserId);
    }
  }, [userRoles, selectedUserId]);

  useEffect(() => {
    if (formData.department_id) {
      loadRoles(formData.department_id);
    } else {
      loadRoles();
    }
  }, [formData.department_id]);

  // Debounced search effect for users
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (userSearchOpen) {
        searchUsers(userSearchQuery);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [userSearchQuery, userSearchOpen]);

  const loadUserRoles = async () => {
    try {
      setLoading(true);
      const response = await userRolesAPI.getAll({
        page: paginationModel.page + 1, // Backend expects 1-based page numbers
        per_page: paginationModel.pageSize,
      });
      setUserRoles(response.data.data || []);
      setRowCount(response.data.total || 0);
    } catch (error) {
      console.error('Error loading user roles:', error);
      setError('Failed to load user roles');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      // Use getAllForPermissions to get all users (both admin and merchant users) for role assignment
      const response = await usersAPI.getAllForPermissions({ is_active: true });
      const usersData = response.data.data || [];
      console.log('🔍 [UserRoles] Loaded users for role assignment:', usersData.length);
      console.log('🔍 [UserRoles] Users data:', usersData);
      
      // Log each user's details for debugging
      usersData.forEach((user, index) => {
        console.log(`🔍 [UserRoles] User ${index + 1}:`, {
          id: user.id,
          name: user.name,
          email: user.email,
          company_id: user.company_id,
          company_name: user.company_name,
          department_id: user.department_id,
          department_name: user.department_name,
          role_id: user.role_id,
          role_name: user.role_name,
          is_active: user.is_active
        });
      });
      
      setUsers(usersData);
      setUserSearchResults(usersData); // Initialize search results with all users
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Failed to load users');
    }
  };

  // Search users function with debouncing
  const searchUsers = async (query) => {
    if (!query || query.length < 2) {
      setUserSearchResults(users);
      return;
    }

    try {
      setUserSearchLoading(true);
      const response = await usersAPI.getAllForPermissions({ 
        is_active: true,
        search: query,
        per_page: 50 // Limit results for better performance
      });
      setUserSearchResults(response.data.data || []);
    } catch (error) {
      console.error('Error searching users:', error);
      setUserSearchResults([]);
    } finally {
      setUserSearchLoading(false);
    }
  };

  const loadRoles = async (departmentId = null) => {
    try {
      const params = departmentId ? { department_id: departmentId } : {};
      // Only fetch active roles (is_active = true)
      params.is_active = true;
      const response = await rolesAPI.getAll(params);
      setRoles(response.data.data || []);
    } catch (error) {
      console.error('Error loading roles:', error);
      setError('Failed to load roles');
    }
  };

  const loadDepartments = async () => {
    try {
      // Only fetch active departments (status = 1 and is_active = true)
      const response = await departmentsAPI.getAll({ status: 1, is_active: true });
      setDepartments(response.data.data || []);
    } catch (error) {
      console.error('Error loading departments:', error);
      setError('Failed to load departments');
    }
  };

  const loadUserRoleDetails = async (userId) => {
    if (!userId) {
      setSelectedUserRoles([]);
      return;
    }
    
    try {
      const filteredRoles = userRoles.filter(role => role.user_id === parseInt(userId));
      setSelectedUserRoles(filteredRoles);
    } catch (error) {
      console.error('Error loading user role details:', error);
      setError('Failed to load user role details');
    }
  };

  const handleUserSelect = (userId) => {
    setSelectedUserId(userId);
    loadUserRoleDetails(userId);
  };

  // Function to check if user already has a role assigned in the same department
  // This allows admin users to have multiple roles from different departments (dual access)
  const checkUserRoleAssignment = async (userId, departmentId = null) => {
    if (!userId) {
      setUserRoleCheck(null);
      setFormError('');
      return;
    }

    try {
      setIsCheckingUser(true);
      setFormError('');
      const params = departmentId ? { department_id: departmentId } : {};
      const response = await userRolesAPI.checkUserRole(userId, params);
      setUserRoleCheck(response.data);
      
      // Only show warning if user has a role in the same department
      if (response.data?.has_role && response.data?.same_department) {
        setFormError(response.data.message);
      }
    } catch (error) {
      console.error('Error checking user role:', error);
      setFormError('Failed to check user role assignment');
    } finally {
      setIsCheckingUser(false);
    }
  };

  // Handle user selection in the form
  const handleFormUserSelect = (userId) => {
    setFormData({ ...formData, user_id: userId, role_id: '', department_id: '' });
    checkUserRoleAssignment(userId);
    
    // Filter departments based on selected user's company
    if (userId) {
      const selectedUser = users.find(user => user.id === userId);
      if (selectedUser && selectedUser.company_id) {
        // Filter departments based on edit mode
        if (editingMapping) {
          // In edit mode, include inactive departments so current assignment is visible
          const filtered = departments.filter(dept => 
            dept.company_id === selectedUser.company_id
          );
          setFilteredDepartments(filtered);
        } else {
          // In create mode, only show active departments
          const filtered = departments.filter(dept => 
            dept.company_id === selectedUser.company_id && 
            dept.is_active !== false && 
            !isInactiveStatus(dept.status)
          );
          setFilteredDepartments(filtered);
        }
      } else {
        setFilteredDepartments([]);
      }
    } else {
      setFilteredDepartments([]);
    }
  };

  const handleAdd = () => {
    setEditingMapping(null);
    setFormData({
      user_id: '',
      role_id: '',
      department_id: '',
      effective_date: '',
      expiry_date: '',
      notes: '',
      is_active: true,
    });
    setUserRoleCheck(null);
    setFormError('');
    setFormErrors({
      user_id: '',
      department_id: '',
      role_id: '',
    });
    setFilteredDepartments([]); // Reset filtered departments
    setDialogOpen(true);
  };

  const handleEdit = (userRoleMapping) => {
    setEditingMapping(userRoleMapping);
    setFormData({
      user_id: userRoleMapping.user_id,
      role_id: userRoleMapping.role_id,
      department_id: userRoleMapping.department_id,
      effective_date: userRoleMapping.effective_date,
      expiry_date: userRoleMapping.expiry_date,
      notes: userRoleMapping.notes,
      is_active: userRoleMapping.is_active,
    });
    setUserRoleCheck(null);
    setFormError('');
    setFormErrors({
      user_id: '',
      department_id: '',
      role_id: '',
    });
    
    // Filter departments based on user's company
    if (userRoleMapping.company_id) {
      // In edit mode, include inactive departments so current assignment is visible
      const filtered = departments.filter(dept => 
        dept.company_id === userRoleMapping.company_id
      );
      setFilteredDepartments(filtered);
    } else {
      setFilteredDepartments([]);
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Run validation
    if (!validateForm()) {
      return;
    }

    // Check if user already has a role assigned in the same department (for new assignments only)
    if (!editingMapping && userRoleCheck?.has_role && userRoleCheck?.same_department) {
      setError('This user already has a role assigned in this department. Please select another department or role.');
      return;
    }

    try {
      if (editingMapping) {
        await userRolesAPI.update(editingMapping.id, {
          role_id: formData.role_id,
          department_id: formData.department_id,
          effective_date: formData.effective_date,
          expiry_date: formData.expiry_date,
          notes: formData.notes,
          is_active: formData.is_active,
        });
        setSuccess(`User role updated successfully. Status: ${formData.is_active ? 'Active' : 'Inactive'}`);
        setDialogOpen(false);
        // Reload user roles data to get updated role names from backend
        loadUserRoles();
      } else {
        await userRolesAPI.assign(formData);
        setSuccess(`Role assigned successfully. Status: ${formData.is_active ? 'Active' : 'Inactive'}`);
        setDialogOpen(false);
        
        // For new assignments, go to first page to see the new item
        setPaginationModel(prev => ({ ...prev, page: 0 }));
      }
    } catch (error) {
      const errorMessage = editingMapping ? 'Failed to update user role' : 'Failed to assign role';
      setError((error.response && error.response.data && error.response.data.error) || errorMessage);
    }
  };

  const handleDelete = (userRoleMapping) => {
    setMappingToDelete(userRoleMapping);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (mappingToDelete) {
      try {
        await userRolesAPI.unassign(mappingToDelete.id);
        setSuccess('Role unassigned successfully');
        setDeleteDialogOpen(false);
        setMappingToDelete(null);
        
        // Check if we need to go back to previous page if current page becomes empty
        const currentPageStartIndex = paginationModel.page * paginationModel.pageSize;
        const totalItemsAfterDelete = rowCount - 1;
        
        if (currentPageStartIndex >= totalItemsAfterDelete && paginationModel.page > 0) {
          setPaginationModel(prev => ({ ...prev, page: prev.page - 1 }));
        } else {
          // Reload user roles data to get updated role names from backend
          loadUserRoles();
        }
      } catch (error) {
        setError('Failed to unassign role');
        setDeleteDialogOpen(false);
        setMappingToDelete(null);
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setMappingToDelete(null);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingMapping(null);
    setError('');
    setUserRoleCheck(null);
    setFormError('');
    setFormErrors({
      user_id: '',
      department_id: '',
      role_id: '',
    });
  };

  // Helper functions to get display lists based on edit mode
  const getDisplayRoles = () => {
    if (editingMapping) {
      // In edit mode, include inactive roles so current assignment is visible
      return roles;
    }
    // In create mode, only show active roles
    return roles.filter(role => role.is_active !== false);
  };

  const getDisplayFilteredDepartments = () => {
    if (editingMapping) {
      // In edit mode, include inactive departments in filtered list
      return filteredDepartments.length > 0 ? filteredDepartments : departments;
    }
    // In create mode, only show active departments
    return (filteredDepartments.length > 0 ? filteredDepartments : departments)
      .filter(dept => dept.is_active !== false && !isInactiveStatus(dept.status));
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'user_name',
      headerName: 'User',
      width: 200,
      type: 'string',
      renderCell: (params) => (
        <Box display="flex" alignItems="center">
          <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
          {params.value}
        </Box>
      ),
    },
    {
      field: 'role_name',
      headerName: 'Role',
      width: 150,
      type: 'string',
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          variant="outlined"
          icon={<SecurityIcon />}
        />
      ),
    },
    {
      field: 'department_name',
      headerName: 'Department',
      width: 150,
      type: 'string',
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          variant="outlined"
          icon={<BusinessIcon />}
        />
      ),
    },
    {
      field: 'company_name',
      headerName: 'Company',
      width: 200,
      renderCell: (params) => (
        <Chip 
          label={params.value}
          variant="outlined"
          size="small"
          color="primary"
        />
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      type: 'string',
      valueGetter: (params) => {
        // Return "Active" or "Inactive" text for filtering instead of boolean
        return params.value ? 'Active' : 'Inactive';
      },
      renderCell: (params) => {
        // Use the raw row data to get the actual boolean value
        const isActive = params.row.is_active;
        return (
          <Chip
            label={isActive ? 'Active' : 'Inactive'}
            color={isActive ? 'success' : 'error'}
            size="small"
          />
        );
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      getActions: (params) => {
        const actions = [];
        
        if (canUpdate) {
          actions.push(
            <Tooltip title="Edit User Role Assignment" arrow>
            <GridActionsCellItem
              icon={<EditIcon />}
              label="Edit"
              onClick={() => handleEdit(params.row)}
            />
            </Tooltip>
          );
        }
        
        if (canDelete) {
          actions.push(
            <Tooltip title="Unassign Role from User" arrow>
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Unassign"
              onClick={() => handleDelete(params.row)}
            />
            </Tooltip>
          );
        }
        
        return actions;
      },
    },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        User Roles Management
      </Typography>
      
      <AutoCloseAlert
        severity="error"
        message={error}
        show={!!error}
        onClose={() => setError('')}
        autoCloseDelay={6000}
      />
      
      <AutoCloseAlert
        severity="success"
        message={success}
        show={!!success}
        onClose={() => setSuccess('')}
        autoCloseDelay={4000}
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ height: 'auto', width: '100%' }}>
                          <DataGrid
                rows={userRoles}
                columns={columns}
                loading={loading}
                rowCount={rowCount}
                paginationModel={paginationModel}
                paginationMode="server"
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[5, 10, 25, 50, 100]}
                disableSelectionOnClick
                components={{
                  Toolbar: () => <CustomToolbar onAdd={handleAdd} hasCreatePermission={canCreate} hasExportPermission={canExport} />
                }}
              />
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                User Role Details
              </Typography>
              
              <Autocomplete
                fullWidth
                sx={{ mb: 2 }}
                options={userSearchResults}
                getOptionLabel={(option) => option ? `${option.name} (${option.email})` : ''}
                value={users.find(user => user.id === selectedUserId) || null}
                onChange={(event, newValue) => {
                  if (newValue) {
                    handleUserSelect(newValue.id);
                  } else {
                    setSelectedUserId('');
                    setSelectedUserRoles([]);
                  }
                }}
                onInputChange={(event, newInputValue) => {
                  setUserSearchQuery(newInputValue);
                }}
                onOpen={() => {
                  setUserSearchOpen(true);
                  if (userSearchResults.length === 0) {
                    setUserSearchResults(users);
                  }
                }}
                onClose={() => {
                  setUserSearchOpen(false);
                  setUserSearchQuery('');
                }}
                loading={userSearchLoading}
                noOptionsText={userSearchLoading ? "Searching..." : "No users found"}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select User"
                    placeholder="Type to search users..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <>
                          {userSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box display="flex" alignItems="center" width="100%">
                      <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Box flexGrow={1}>
                        <Typography variant="body2" fontWeight="medium">
                          {option.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.email}
                        </Typography>
                        {option.company_name && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {option.company_name}
                          </Typography>
                        )}
                        {option.is_active === false && (
                          <Chip label="Inactive" size="small" color="error" sx={{ mt: 0.5 }} />
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}
              />

              {selectedUserId && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Current Role Assignments:
                  </Typography>
                  
                  {selectedUserRoles.length > 0 ? (
                    <List>
                      {selectedUserRoles.map((roleAssignment) => (
                        <ListItem key={roleAssignment.id} sx={{ px: 0 }}>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <SecurityIcon sx={{ color: 'primary.main', fontSize: 16 }} />
                                <Typography variant="body2" fontWeight="bold">
                                  {roleAssignment.role_name}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Box display="flex" alignItems="center" gap={1} sx={{ mt: 0.5 }}>
                                <BusinessIcon sx={{ color: 'text.secondary', fontSize: 14 }} />
                                <Typography variant="caption">
                                  {roleAssignment.department_name}
                                </Typography>
                              </Box>
                            }
                          />
                          <ListItemSecondaryAction>
                            <Chip
                              label={isActiveStatus(roleAssignment.status) ? 'Active' : 'Inactive'}
                              color={isActiveStatus(roleAssignment.status) ? 'success' : 'error'}
                              size="small"
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      No role assignments found for this user.
                    </Typography>
                  )}
                </Box>
              )}

              {!selectedUserId && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Type to search and select a user to view their role assignments.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <AssignmentIcon sx={{ mr: 1 }} />
            {editingMapping ? 'Edit User Role' : 'Assign Role to User'}
          </Box>
        </DialogTitle>
        <form onSubmit={handleSubmit} noValidate>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            
            {formError && (
              <Alert severity="warning" sx={{ mb: 2 }}>{formError}</Alert>
            )}
            
            {editingMapping && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>Edit Mode:</strong> Inactive users, departments, and roles are shown to allow editing of current assignments. 
                You can change to active entities if needed.
              </Alert>
            )}
            
            <Autocomplete
              fullWidth
              sx={{ mb: 2 }}
              options={userSearchResults}
              getOptionLabel={(option) => option ? `${option.name} (${option.email})` : ''}
              value={users.find(user => user.id === formData.user_id) || null}
              onChange={(event, newValue) => {
                if (newValue) {
                  handleFormUserSelect(newValue.id);
                } else {
                  setFormData({ ...formData, user_id: '', role_id: '', department_id: '' });
                  setUserRoleCheck(null);
                  setFormError('');
                }
              }}
              onInputChange={(event, newInputValue) => {
                setUserSearchQuery(newInputValue);
              }}
              onOpen={() => {
                setUserSearchOpen(true);
                if (userSearchResults.length === 0) {
                  setUserSearchResults(users);
                }
              }}
              onClose={() => {
                setUserSearchOpen(false);
                setUserSearchQuery('');
              }}
              loading={userSearchLoading}
              disabled={editingMapping !== null}
              noOptionsText={userSearchLoading ? "Searching..." : "No users found"}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="User"
                  error={!!formErrors.user_id}
                  helperText={formErrors.user_id || "Search and select a user to assign role"}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {userSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  FormHelperTextProps={{
                    sx: {
                      color: formErrors.user_id ? 'error.main' : 'text.secondary',
                      fontWeight: formErrors.user_id ? 500 : 'normal'
                    }
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box display="flex" alignItems="center" width="100%">
                    <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Box flexGrow={1}>
                      <Typography variant="body2" fontWeight="medium">
                        {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.email}
                      </Typography>
                      {option.company_name && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {option.company_name}
                        </Typography>
                      )}
                      {option.is_active === false && (
                        <Chip label="Inactive" size="small" color="error" sx={{ mt: 0.5 }} />
                      )}
                    </Box>
                  </Box>
                </Box>
              )}
            />
            {isCheckingUser && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Checking user role assignment...
              </Typography>
            )}

            <FormControl fullWidth sx={{ mb: 2 }} error={!!formErrors.department_id}>
              <InputLabel>Department</InputLabel>
              <Select
                value={formData.department_id}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({ ...formData, department_id: value, role_id: '' });
                  // Clear error when user selects a department
                  if (formErrors.department_id) {
                    setFormErrors({ ...formErrors, department_id: '' });
                  }
                  // Re-check user role assignment with the selected department
                  if (formData.user_id && value) {
                    checkUserRoleAssignment(formData.user_id, value);
                  }
                }}
                onBlur={() => {
                  // Validate on blur
                  const fieldErrors = validateField('department_id', formData.department_id);
                  if (Object.keys(fieldErrors).length > 0) {
                    setFormErrors(prev => ({ ...prev, ...fieldErrors }));
                  }
                }}
                label="Department"
                disabled={!formData.user_id || (userRoleCheck?.has_role && userRoleCheck?.same_department)}
              >
                <MenuItem value="">
                  <em>Select Department</em>
                </MenuItem>
                {/* Show filtered departments, or all departments if filtered list is empty */}
                {/* This ensures departments are always available even if filtering fails */}
                {getDisplayFilteredDepartments().map((department) => (
                  <MenuItem key={department.id} value={department.id}>
                    {department.department_name} {department.is_active === false && '(Inactive)'}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.department_id && (
                <Box sx={{ 
                  color: 'error.main', 
                  fontSize: '0.75rem', 
                  mt: 0.5, 
                  ml: 1.75,
                  fontWeight: 500
                }}>
                  {formErrors.department_id}
                </Box>
              )}
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }} error={!!formErrors.role_id}>
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role_id}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({ ...formData, role_id: value });
                  // Clear error when user selects a role
                  if (formErrors.role_id) {
                    setFormErrors({ ...formErrors, role_id: '' });
                  }
                }}
                onBlur={() => {
                  // Validate on blur
                  const fieldErrors = validateField('role_id', formData.role_id);
                  if (Object.keys(fieldErrors).length > 0) {
                    setFormErrors(prev => ({ ...prev, ...fieldErrors }));
                  }
                }}
                label="Role"
                disabled={!formData.department_id || (userRoleCheck?.has_role && userRoleCheck?.same_department)}
              >
                <MenuItem value="">
                  <em>{formData.department_id ? 'Select Role' : 'Select Department First'}</em>
                </MenuItem>
                {getDisplayRoles().map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.role_name} {role.is_active === false && '(Inactive)'}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.role_id && (
                <Box sx={{ 
                  color: 'error.main', 
                  fontSize: '0.75rem', 
                  mt: 0.5, 
                  ml: 1.75,
                  fontWeight: 500
                }}>
                  {formErrors.role_id}
                </Box>
              )}
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  disabled={userRoleCheck?.has_role && userRoleCheck?.same_department}
                />
              }
              label="Active Status"
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained"
              disabled={userRoleCheck?.has_role && userRoleCheck?.same_department}
            >
              {editingMapping ? 'Update Role' : 'Assign Role'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          color: 'error.main', 
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <DeleteIcon />
          Confirm Unassign Role
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '1.1rem', color: 'text.primary' }}>
            Are you sure you want to unassign the role{' '}
            <strong>{mappingToDelete?.role_name}</strong> from{' '}
            <strong>{mappingToDelete?.user_name}</strong>{' '}
            in the <strong>{mappingToDelete?.department_name}</strong> department?
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, color: 'text.secondary' }}>
            This action will mark the role assignment as inactive and remove the user's access to resources associated with this role. The assignment can be reactivated later if needed.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={handleCancelDelete}
            variant="outlined"
            size="large"
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            size="large"
            sx={{ minWidth: 100 }}
            autoFocus
          >
            Unassign Role
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserRoles; 